import React, { useEffect, useState, useCallback, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthHandler';
import { useStageCatalog } from '../../hooks/useTicketStages';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faVolumeHigh,
  faVolumeXmark,
  faTicketAlt,
  faUsersCog,
  faChartLine,
  faCalendarAlt,
  faBars,
  faAngleDown,
  faAngleRight,
  faUser,
  faSignOutAlt,
  faSquarePlus,
  faComment,
  faCommentSlash,
  faSitemap,
  faLinesLeaning,
  faChartGantt,
  faChartDiagram,
  faPersonThroughWindow,
  faFileText,
  faChartColumn
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import '../../styling/Header.css';
import { useApiClient } from '../../service/API';
import { useWebSocketContext } from '../WebSocket/WebSocketContext';
import { useSidebar } from './SidebarContext';

function Header({ onLogout }) {
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const navigate = useNavigate();

  const {
    authenticated, userEmail, userName, userRoles,
    mute, setMute, popup, setPopup
  } = useAuth();

  const [alerts, setAlerts] = useState([]);
  const [msgAlerts, setMsgAlerts] = useState([]);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const apiClient = useApiClient();
  const apiRef = useRef(apiClient);
  useEffect(() => { apiRef.current = apiClient; }, [apiClient]);

  const { isConnected, alertMessage, latestTicketEvents } = useWebSocketContext();

  const ONE_DAY_MS = 24 * 60 * 60 * 1000;

  const SGT_OFFSET = 8 * 60 * 60 * 1000;

  const visibleMsgAlerts = React.useMemo(() => {
    const now = Date.now();

    const getSGTDate = (dateStr) => {
      if (!dateStr) return null;

      const utcDate = new Date(
        dateStr.endsWith("Z") ? dateStr : `${dateStr}Z`
      );

      return new Date(utcDate.getTime() + SGT_OFFSET);
    };

    const getExpiryTime = (alert) => {
      const baseDateStr = alert.updatedDateTime ?? alert.createdDateTime;

      const updatedAtSGT = getSGTDate(baseDateStr);
      const sentAtSGT = getSGTDate(baseDateStr);

      if (!updatedAtSGT) return Number.MAX_SAFE_INTEGER;

      const day = sentAtSGT.getDay();
      const hour = sentAtSGT.getHours();

      const isFridayAfter6 = day === 5 && hour >= 18;
      const isWeekend = day === 6 || day === 0;

      if (isFridayAfter6 || isWeekend) {
        const monday = new Date(sentAtSGT);
        const sentDay = monday.getDay();

        const daysUntilMonday =
          sentDay === 0 ? 1 : sentDay === 6 ? 2 : 3;

        monday.setDate(monday.getDate() + daysUntilMonday);
        monday.setHours(0, 0, 0, 0);

        const expiry = new Date(monday);
        expiry.setDate(expiry.getDate() + 1);

        return expiry.getTime();
      }

      return updatedAtSGT.getTime() + ONE_DAY_MS;
    };

    return msgAlerts.filter((a) => {
      if (a.type !== "MESSAGE") return false;

      console.log('-=====================-');
      console.log(a.message);
      console.log(a.createdDateTime);
      console.log(a.updatedDateTime);
      console.log(now);
      console.log(getExpiryTime(a));

      return !a.messageRead || now <= getExpiryTime(a);
    });
  }, [msgAlerts]);

  useEffect(() => {
    if (sidebarOpen === false) {
      setUserDropdownOpen(false);
    }
  }, [sidebarOpen]);

  const fetchHistoryNotification = useCallback(async () => {
    try {
      const response = await apiRef.current.get(`tickets/notify/notification`);
      const historyMsg = response.data;
      setAlerts(historyMsg.filter(msg => !(['LEAVE', 'LEAVE-REQUEST'].includes(msg.type) && msg.messageRead)));
    } catch {
      // silent
    }
  }, []);

  const fetchHistoryMsgNotification = useCallback(async () => {
    try {
      const response = await apiRef.current.get(`tickets/notify/msg-notification`);
      setMsgAlerts(response.data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    const syncHistory = () => {
      if (!((userRoles.length === 1 && ['USER'].some(role => userRoles.includes(role)) || userRoles.length === 0))
        && isConnected
        && document.visibilityState === 'visible') {
        fetchHistoryNotification();
        fetchHistoryMsgNotification();
      }
    };

    window.addEventListener('focus', syncHistory);
    window.addEventListener('online', syncHistory);
    document.addEventListener('visibilitychange', syncHistory);

    syncHistory();

    return () => {
      window.removeEventListener('focus', syncHistory);
      window.removeEventListener('online', syncHistory);
      document.removeEventListener('visibilitychange', syncHistory);
    };
  }, [isConnected, fetchHistoryNotification, fetchHistoryMsgNotification, userRoles]);

  const onMessageReceivedForAlert = useCallback((msg, shouldToast = true) => {
    if (!msg) return;

    if (msg.type === 'MESSAGE') {
      setMsgAlerts(prev => {
        if (prev.some(a => a.id === msg.id)) return prev;
        if (shouldToast && msg.ticketId != null) toast.success(`Alert received for TicketId: ${msg.ticketId}`);
        return [msg, ...prev];
      });
    } else {
      setAlerts(prev => {
        const isDuplicate = prev.some(a => a.id === msg.id);
        if (!isDuplicate) {
          if (shouldToast && msg.ticketId != null) toast.success(`Alert received for TicketId: ${msg.ticketId}`);
          return [msg, ...prev];
        }
        return prev.map(a => a.id === msg.id ? { ...a, messageRead: msg.messageRead } : a);
      });
    }
  }, []);

  useEffect(() => {
    if (alertMessage) onMessageReceivedForAlert(alertMessage, true);
    if (latestTicketEvents) onMessageReceivedForAlert(latestTicketEvents, false); // <- no toast
  }, [alertMessage, latestTicketEvents, onMessageReceivedForAlert]);

  const handleClick = async (ticketId, msgId) => {
    try {
      const res = await apiRef.current.put(`/tickets/chat/update-read-status/${msgId}`);
      if (res?.status === 204) {
        setMsgAlerts(prev =>
          prev.map(m =>
            m.msgId === msgId ? { ...m, messageRead: true } : m
          )
        );
      }
    } catch (e) {
      // optional: toast error / log
    } finally {
      navigate(`/view-ticket/${ticketId}`);
      setNotificationsOpen(false);
    }
  };

  const handleClickTicket = (ticketId, id, status) => {
    const updatedMsgAlerts = alerts.map((alert) => {
      if (alert.id === id) {
        apiRef.current.put(`/tickets/notify/update-read-ticketStatus/${alert.id}`).catch(() => { });
        return { ...alert, messageRead: true };
      }
      return alert;
    });

    setAlerts(updatedMsgAlerts);
    if (ticketId) {
      navigate(`/view-ticket/${ticketId}`);
    }

    if (status === 'LEAVE-REQUEST' && ['BOMANAGER', 'ADMIN'].some(role => userRoles.includes(role))) {
      navigate('/manage-leave');
    }
    setNotificationsOpen(false);
  };

  useEffect(() => {
    toast.dismiss();
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
    setNotificationsOpen(false);
  }, [setSidebarOpen]);

  const toggleUserDropdown = useCallback(() => {
    setSidebarOpen(true);
    setUserDropdownOpen(prev => !prev);
  }, [setSidebarOpen]);

  const toggleNotifications = useCallback(() => {
    setSidebarOpen(true);
    setNotificationsOpen(prev => !prev);
  }, [setSidebarOpen]);

  const closeMenusOnNav = () => {
    setUserDropdownOpen(false);
  };

  const goCreateTicket = useCallback(() => {
    closeMenusOnNav();
    navigate(`/create-ticket?key=${Date.now()}`);
  }, [navigate]);

  const handleLogoutClick = (e) => {
    e.preventDefault();
    const confirmLogout = window.confirm('Are you sure you want to log out?');
    if (confirmLogout) {
      onLogout();
      navigate('/login');
    }
  };

  const updateEnableNotification = async (e) => {
    e.preventDefault();
    try {
      const response = await apiRef.current.patch('setting/popup');
      if (response.status === 200) setPopup(response.data);
    } catch { }
  };

  const updateMute = async (e) => {
    e.preventDefault();
    try {
      const response = await apiRef.current.patch('setting/mute');
      if (response.status === 200) setMute(response.data);
    } catch { }
  };

  const unreadNotificationsCount = new Set([
    ...alerts
      .filter(a => a.messageRead !== true && a.id != null)
      .map(a => a.id),
    ...visibleMsgAlerts
      .filter(a => a.messageRead !== true && a.id != null)
      .map(a => a.id),
  ]).size;

  const markAllMessagesRead = useCallback(async () => {
    try {
      await apiRef.current.put('tickets/notify/update-msg-read-all');
      setMsgAlerts(prev => prev.map(m => ({ ...m, messageRead: true })));
      toast.success('All message notifications marked as read');
    } catch {
      toast.error('Failed to mark message notifications as read');
    }
  }, []);

  const markAllTicketsRead = useCallback(async () => {
    try {
      await apiRef.current.put('tickets/notify/update-read-all');
      setAlerts(prev => prev.map(a => ({ ...a, messageRead: true })));
      toast.success('All ticket notifications marked as read');
    } catch {
      toast.error('Failed to mark ticket notifications as read');
    }
  }, []);

  const stripHtml = (text) => {
    return String(text ?? "").replace(/<\/?[^>]+(>|$)/g, "");
  };

  return (
    <div className={`sidebar bg-light ${sidebarOpen ? 'open' : 'closed'}`}>
      {/* Logo and Toggle */}
      <div className={`d-flex ${sidebarOpen ? 'p-2' : 'p-1'} border-bottom sidebar-top`}>
        {sidebarOpen && (
          <div className="navbar-brand">
            <img src="/image/logo.png" alt="Logo" style={{ height: '40px' }} />
          </div>
        )}
        <button className="btn btn-light" onClick={toggleSidebar} aria-label="Toggle sidebar">
          <FontAwesomeIcon icon={faBars} />
        </button>
      </div>

      {authenticated && (
        <div className="d-flex flex-column justify-content-between h-100 py-2">
          <ul className="nav flex-column">
            {/* Create Ticket */}
            {['BOMANAGER', 'FOMANAGER', 'FOUSER', 'ADMIN'].some(role => userRoles.includes(role)) && (
              <li className="nav-item">
                <NavLink
                  className="nav-link"
                  to="/create-ticket"
                  onClick={(e) => { e.preventDefault(); goCreateTicket(); }}
                  title={!sidebarOpen ? 'Create Ticket' : undefined}
                >
                  <span className="nav-icon"><FontAwesomeIcon icon={faSquarePlus} /></span>
                  {sidebarOpen && <span className="nav-text">Create Ticket</span>}
                </NavLink>
              </li>
            )}

            {/* Tickets dropdown */}
            {['BOMANAGER', 'FOMANAGER', 'BOUSER', 'FOUSER', 'ADMIN'].some(role => userRoles.includes(role)) && (
              <li className="nav-item">
                <NavLink
                    className="nav-link flex-grow-1"
                    to="/"
                    onClick={closeMenusOnNav}
                    title={!sidebarOpen ? 'All Tickets' : undefined}
                  >
                    <span className="nav-icon"><FontAwesomeIcon icon={faTicketAlt} /></span>
                    {sidebarOpen && <span className="nav-text">All Tickets</span>}
                  </NavLink>
              </li>
            )}

            {/* Extract Text */}
            {['BOMANAGER', 'BOUSER', 'ADMIN'].some(role => userRoles.includes(role)) && (
              <li className="nav-item">
                <NavLink className="nav-link" to="/extract-text" onClick={closeMenusOnNav} title={!sidebarOpen ? 'Extract Text' : undefined}>
                  <span className="nav-icon"><FontAwesomeIcon icon={faFileText} /></span>
                  {sidebarOpen && <span className="nav-text">Extract Text</span>}
                </NavLink>
              </li>
            )}

            {/* Admin links */}
            {userRoles.includes('ADMIN') && (
              <>
                <li className="nav-item">
                  <NavLink className="nav-link" to="/company-domains" onClick={closeMenusOnNav} title={!sidebarOpen ? 'Company Domain' : undefined}>
                    <span className="nav-icon"><FontAwesomeIcon icon={faLinesLeaning} /></span>
                    {sidebarOpen && <span className="nav-text">Company Domain</span>}
                  </NavLink>
                </li>

                <li className="nav-item">
                  <NavLink className="nav-link" to="/manage-user" onClick={closeMenusOnNav} title={!sidebarOpen ? 'Assign Role' : undefined}>
                    <span className="nav-icon"><FontAwesomeIcon icon={faUsersCog} /></span>
                    {sidebarOpen && <span className="nav-text">Assign Role</span>}
                  </NavLink>
                </li>

                <li className="nav-item">
                  <NavLink className="nav-link" to="/holiday" onClick={closeMenusOnNav} title={!sidebarOpen ? 'Manage Holiday' : undefined}>
                    <span className="nav-icon"><FontAwesomeIcon icon={faCalendarAlt} /></span>
                    {sidebarOpen && <span className="nav-text">Manage Holiday</span>}
                  </NavLink>
                </li>

                <li className="nav-item">
                  <NavLink className="nav-link" to="/service-flows/*" onClick={closeMenusOnNav} title={!sidebarOpen ? 'Manage Service Flow' : undefined}>
                    <span className="nav-icon"><FontAwesomeIcon icon={faSitemap} /></span>
                    {sidebarOpen && <span className="nav-text">Manage Service Flow</span>}
                  </NavLink>
                </li>
              </>
            )}

            {/* Manager/Admin */}
            {['BOMANAGER', 'ADMIN'].some(role => userRoles.includes(role)) && (
              <>
                <li className="nav-item">
                  <NavLink className="nav-link" to="/performance-report" onClick={closeMenusOnNav} title={!sidebarOpen ? 'Performance Report' : undefined}>
                    <span className="nav-icon"><FontAwesomeIcon icon={faChartGantt} /></span>
                    {sidebarOpen && <span className="nav-text">Performance Report</span>}
                  </NavLink>
                </li>

                <li className="nav-item">
                  <NavLink className="nav-link" to="/urgent-ticket-report" onClick={closeMenusOnNav} title={!sidebarOpen ? 'Performance Report' : undefined}>
                    <span className="nav-icon"><FontAwesomeIcon icon={faChartColumn} /></span>
                    {sidebarOpen && <span className="nav-text">Urgent Ticket Report</span>}
                  </NavLink>
                </li>

                <li className="nav-item">
                  <NavLink className="nav-link" to="/manage-leave" onClick={closeMenusOnNav} title={!sidebarOpen ? 'Manage Leave' : undefined}>
                    <span className="nav-icon"><FontAwesomeIcon icon={faPersonThroughWindow} /></span>
                    {sidebarOpen && <span className="nav-text">Manage Leave</span>}
                  </NavLink>
                </li>

                <li className="nav-item">
                  <NavLink className="nav-link" to="/assignment-preview" onClick={closeMenusOnNav} title={!sidebarOpen ? 'Manage Assign Ticket' : undefined}>
                    <span className="nav-icon"><FontAwesomeIcon icon={faChartDiagram} /></span>
                    {sidebarOpen && <span className="nav-text">Manage Assign Ticket</span>}
                  </NavLink>
                </li>

                <li className="nav-item">
                  <NavLink className="nav-link" to="/dashboard/bo" onClick={closeMenusOnNav} title={!sidebarOpen ? 'BO Dashboard' : undefined}>
                    <span className="nav-icon"><FontAwesomeIcon icon={faChartLine} /></span>
                    {sidebarOpen && <span className="nav-text">BO Dashboard</span>}
                  </NavLink>
                </li>
              </>
            )}
          </ul>

          {/* Bottom */}
          <div className="nav flex-column">
            {/* Notifications */}
            <div className="nav-item position-relative">
              <div
                className="nav-link"
                onClick={toggleNotifications}
                style={{ cursor: 'pointer' }}
                title={!sidebarOpen ? 'Notifications' : undefined}
              >
                <span className="nav-icon">
                  <span className="notify-icon-wrap">
                    <FontAwesomeIcon icon={faBell} />
                    {unreadNotificationsCount > 0 && (
                      <span className="notify-badge">{unreadNotificationsCount}</span>
                    )}
                  </span>
                </span>
                {sidebarOpen && <span className="nav-text">Notifications</span>}
              </div>

              {/* Notifications Dropdown */}
              {notificationsOpen && (
                <div className="sidebar-notify-panel">
                  <div className="sidebar-notify-panel__content">

                    <div className="dropdown-header mt-2">
                      <h6 className='mt-2'>Message Notification</h6>
                      <u
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); markAllMessagesRead(); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') markAllMessagesRead(); }}
                      >
                        Mark All As Read
                      </u>
                    </div>

                    <div className="sidebar-notify-panel__section scroll message-content">
                      {visibleMsgAlerts.length > 0 ? (
                        visibleMsgAlerts.map((alert, index) => {
                          if (!alert.id) return null;
                          
                          return (
                            <div
                              key={`${alert.ticketId}-${index}`}
                              className={`dropdown-item alert ${alert.messageRead ? '' : 'MESSAGE'} ${alert.messageRead ? '' : 'unread'}`}
                              onClick={() => handleClick(alert.ticketId, alert.msgId)}
                            >
                              <span className="pill">Ticket #{alert.ticketId}</span>
                              {stripHtml(alert.message)}
                            </div>
                          );
                        })
                      ) : (
                        <div className="dropdown-item">No unread message notifications</div>
                      )}
                    </div>

                    <div className="dropdown-header">
                      <h6 className='mt-2'>Ticket Notification</h6>
                      <u
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); markAllTicketsRead(); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') markAllTicketsRead(); }}
                      >
                        Mark All As Read
                      </u>
                    </div>                    <div className="sidebar-notify-panel__section scroll ticket-notification-content">
                      {alerts.length > 0 ? (
                        alerts.map((alert, index) => {
                          if (!alert.id) return null;

                          return (
                            <div
                              key={`${alert.ticketId}-${index}`}
                              className={`dropdown-item ${alert.messageRead ? '' : alert.message.includes("REASSIGNED") ? "REASSINGED" : alert.type} ${alert.messageRead ? '' : 'unread'} alert`}
                              onClick={() => handleClickTicket(alert.ticketId, alert.id, alert.type)}
                            >
                              <span className="pill">
                                {['LEAVE', 'LEAVE-REQUEST'].includes(alert.type)
                                  ? 'Leave'
                                  : `Ticket #${alert.ticketId}`}
                              </span>
                              {stripHtml(alert.message)}
                            </div>
                          );
                        })
                      ) : (
                        <div className="dropdown-item">No ticket notifications</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* User */}
            <div className="nav-item">
              <div className="nav-link" onClick={toggleUserDropdown} style={{ cursor: 'pointer' }} title={!sidebarOpen ? 'User Menu' : undefined}>
                <span className="nav-icon"><FontAwesomeIcon icon={faUser} /></span>
                {sidebarOpen && (
                  <>
                    <span className="nav-text">{userName}</span>
                    <FontAwesomeIcon icon={userDropdownOpen ? faAngleDown : faAngleRight} className="ms-auto" />
                  </>
                )}
              </div>

              {userDropdownOpen && (
                <ul className={`nav flex-column ${sidebarOpen ? 'ms-4' : ''}`} style={{
                  backgroundColor: !sidebarOpen ? '#fff' : 'transparent',
                  boxShadow: !sidebarOpen ? '0 0 10px rgba(0,0,0,0.2)' : 'none',
                  left: '60px',
                }}>
                  <li className="nav-item">
                    <div className="nav-link text-muted">{userEmail}</div>
                  </li>
                  {['BOUSER', 'ADMIN'].some(role => userRoles.includes(role)) && (
                    <li className="nav-item">
                      <NavLink className="nav-link" to="/leave" onClick={closeMenusOnNav}>Leave</NavLink>
                    </li>
                  )}
                  <li className="nav-item">
                    <button className="nav-link btn btn-link p-0 text-start" onClick={handleLogoutClick}>
                      <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                      Logout
                    </button>
                  </li>
                </ul>
              )}
            </div>

            {/* Popups */}
            {!['BOUSER'].some(role => userRoles.includes(role)) && (
              <div className="nav-item">
                <button className="nav-link nav-link-button" onClick={updateEnableNotification} title={!sidebarOpen ? 'Pop-Ups' : undefined}>
                  <span className="nav-icon"><FontAwesomeIcon icon={popup ? faComment : faCommentSlash} /></span>
                  {sidebarOpen && <span className="nav-text">Pop-Ups {popup ? 'Enabled' : 'Disabled'}</span>}
                </button>
              </div>
            )}

            {/* Sound */}
            <div className="nav-item">
              <button className="nav-link nav-link-button" onClick={updateMute} title={!sidebarOpen ? 'Sound' : undefined}>
                <span className="nav-icon"><FontAwesomeIcon icon={mute ? faVolumeXmark : faVolumeHigh} /></span>
                {sidebarOpen && <span className="nav-text">Sound {mute ? 'Disabled' : 'Enabled'}</span>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Header;
